/**
 * ============================================
 * UCM - Ultra Car Manager
 * Public Website Layout - Premium Version
 * Supports both live API and static data mode
 * © 2024 AlbaSoft Studio - All Rights Reserved
 * ============================================
 */

import { createContext, useContext, useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import CompareBar from './CompareBar';
import WhatsAppButton from '../WhatsAppButton';
import { isStaticMode, fetchStaticData } from '../../api/client';

// Detect if we're in static mode (Cloudflare Pages deployment)
const STATIC_MODE = isStaticMode();

// API URL - only used in development (localhost)
export const API_BASE_URL = STATIC_MODE ? '' : 'http://localhost:4001/api/v1';
export const TENANT_SLUG = 'demo-autonoleggio'; // Default tenant

// Vehicle type for compare list
export interface CompareVehicle {
  id: string;
  brand: string;
  model: string;
  year?: number | null;
  dailyRate?: number | null;
  images?: string[];
  [key: string]: unknown;
}

// Context per preferiti e confronta
interface ShowroomContextType {
  favorites: string[];
  addFavorite: (id: string) => void;
  removeFavorite: (id: string) => void;
  toggleFavorite: (id: string) => void;
  isFavorite: (id: string) => boolean;
  compareList: CompareVehicle[];
  addToCompare: (vehicle: CompareVehicle) => void;
  removeFromCompare: (id: string) => void;
  isInCompare: (id: string) => boolean;
  clearCompare: () => void;
  settings: WebsiteSettings | null;
}

interface WebsiteSettings {
  general: {
    siteName: string;
    siteSlogan: string;
    siteDescription: string;
    logo: string;
    logoDark: string;
    favicon: string;
    primaryColor: string;
    secondaryColor: string;
    accentColor: string;
    fontHeading: string;
    fontBody: string;
  };
  contact: {
    phone: string;
    phoneSecondary: string;
    email: string;
    whatsapp: string;
    address: string;
    city: string;
    province: string;
    cap: string;
    googleMapsUrl: string;
    businessHours: string;
  };
  social: {
    facebook: string;
    instagram: string;
    youtube: string;
    linkedin: string;
    tiktok: string;
  };
  seo: {
    metaTitle: string;
    metaDescription: string;
    metaKeywords: string;
    ogImage: string;
  };
  homepage: {
    heroTitle: string;
    heroSubtitle: string;
    heroImage: string;
    heroVideo: string;
    heroBackgroundType: 'image' | 'video' | 'gradient';
    heroBanner: {
      enabled: boolean;
      image: string;
      title: string;
      subtitle: string;
      link: string;
      linkText: string;
      position: 'left' | 'right' | 'center';
      style: 'minimal' | 'card' | 'floating';
    };
    showStats: boolean;
    showFeaturedVehicles: boolean;
    featuredVehiclesCount: number;
    showServices: boolean;
  };
}

const ShowroomContext = createContext<ShowroomContextType | null>(null);

export const useShowroom = () => {
  const context = useContext(ShowroomContext);
  if (!context) {
    throw new Error('useShowroom must be used within ShowroomProvider');
  }
  return context;
};

export default function PublicLayout() {
  const [favorites, setFavorites] = useState<string[]>([]);
  const [compareList, setCompareList] = useState<CompareVehicle[]>([]);
  const [settings, setSettings] = useState<WebsiteSettings | null>(null);

  // Load from localStorage
  useEffect(() => {
    const savedFavorites = localStorage.getItem('ucm_favorites');
    const savedCompare = localStorage.getItem('ucm_compare');
    if (savedFavorites) setFavorites(JSON.parse(savedFavorites));
    if (savedCompare) setCompareList(JSON.parse(savedCompare));

    // Load settings
    loadSettings();
  }, []);

  // Save to localStorage
  useEffect(() => {
    localStorage.setItem('ucm_favorites', JSON.stringify(favorites));
  }, [favorites]);

  useEffect(() => {
    localStorage.setItem('ucm_compare', JSON.stringify(compareList));
  }, [compareList]);

  // Apply CSS variables for theming
  const applyThemeColors = (primary: string, secondary: string, accent: string) => {
    const root = document.documentElement;

    // Helper to convert hex to RGB
    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? `${parseInt(result[1], 16)} ${parseInt(result[2], 16)} ${parseInt(result[3], 16)}` : null;
    };

    if (primary) {
      root.style.setProperty('--color-primary', primary);
      const rgb = hexToRgb(primary);
      if (rgb) root.style.setProperty('--color-primary-rgb', rgb);
    }
    if (secondary) {
      root.style.setProperty('--color-secondary', secondary);
      const rgb = hexToRgb(secondary);
      if (rgb) root.style.setProperty('--color-secondary-rgb', rgb);
    }
    if (accent) {
      root.style.setProperty('--color-accent', accent);
      const rgb = hexToRgb(accent);
      if (rgb) root.style.setProperty('--color-accent-rgb', rgb);
    }
  };

  // Apply dynamic fonts from Google Fonts
  const applyThemeFonts = (fontHeading: string, fontBody: string) => {
    const root = document.documentElement;

    // Load Google Fonts dynamically
    const fontsToLoad = new Set([fontHeading, fontBody].filter(Boolean));
    const existingLinks = document.querySelectorAll('link[data-ucm-font]');
    existingLinks.forEach(link => link.remove());

    if (fontsToLoad.size > 0) {
      const fontFamilies = Array.from(fontsToLoad)
        .map(font => font.replace(/\s+/g, '+') + ':wght@400;500;600;700')
        .join('&family=');

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${fontFamilies}&display=swap`;
      link.setAttribute('data-ucm-font', 'true');
      document.head.appendChild(link);
    }

    // Apply CSS variables for fonts
    if (fontHeading) {
      root.style.setProperty('--font-heading', `"${fontHeading}", sans-serif`);
    }
    if (fontBody) {
      root.style.setProperty('--font-body', `"${fontBody}", sans-serif`);
    }
  };

  const loadSettings = async () => {
    try {
      let tenant = null;

      if (STATIC_MODE) {
        // Load from static JSON files (Cloudflare Pages)
        tenant = await fetchStaticData<any>('tenant.json');
      } else {
        // Load from live API (localhost development)
        const response = await fetch(`${API_BASE_URL}/public/tenant`, {
          headers: {
            'X-Tenant-Slug': TENANT_SLUG,
          },
        });
        const data = await response.json();
        if (data.success && data.data) {
          tenant = data.data;
        }
      }

      if (tenant) {

        // Get colors from CMS settings
        const primaryColor = tenant.primaryColor || '#2563eb';
        const secondaryColor = tenant.secondaryColor || '#1e40af';
        const accentColor = tenant.accentColor || '#f59e0b';

        // Get fonts from CMS settings
        const fontHeading = tenant.fontHeading || 'Inter';
        const fontBody = tenant.fontBody || 'Inter';

        // Apply theme colors
        applyThemeColors(primaryColor, secondaryColor, accentColor);

        // Apply theme fonts
        applyThemeFonts(fontHeading, fontBody);

        // Update favicon dynamically
        if (tenant.favicon) {
          const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement || document.createElement('link');
          link.rel = 'icon';
          link.href = tenant.favicon;
          document.head.appendChild(link);
        }

        setSettings({
          general: {
            siteName: tenant.name || 'Auto & Noleggio',
            siteSlogan: tenant.slogan || '',
            siteDescription: tenant.description || '',
            logo: tenant.logo || '',
            logoDark: tenant.logoDark || '',
            favicon: tenant.favicon || '',
            primaryColor,
            secondaryColor,
            accentColor,
            fontHeading,
            fontBody,
          },
          contact: {
            phone: tenant.phone || '',
            phoneSecondary: tenant.phoneSecondary || '',
            email: tenant.email || '',
            whatsapp: tenant.whatsapp || tenant.phone || '',
            address: tenant.address || '',
            city: tenant.city || '',
            province: tenant.provincia || '',
            cap: tenant.cap || '',
            googleMapsUrl: tenant.googleMapsUrl || '',
            businessHours: typeof tenant.businessHours === 'string'
              ? tenant.businessHours
              : tenant.businessHours?.weekdays || '9:00-18:00',
          },
          social: {
            facebook: tenant.socialLinks?.facebook || '',
            instagram: tenant.socialLinks?.instagram || '',
            youtube: tenant.socialLinks?.youtube || '',
            linkedin: tenant.socialLinks?.linkedin || '',
            tiktok: tenant.socialLinks?.tiktok || '',
          },
          seo: {
            metaTitle: tenant.metaTitle || tenant.name || 'Auto & Noleggio',
            metaDescription: tenant.metaDescription || tenant.description || '',
            metaKeywords: tenant.metaKeywords || '',
            ogImage: tenant.ogImage || tenant.logo || '',
          },
          homepage: {
            heroTitle: '',
            heroSubtitle: '',
            heroImage: '',
            heroVideo: '',
            heroBackgroundType: 'gradient',
            heroBanner: {
              enabled: false,
              image: '',
              title: '',
              subtitle: '',
              link: '',
              linkText: '',
              position: 'right',
              style: 'card',
            },
            showStats: true,
            showFeaturedVehicles: true,
            featuredVehiclesCount: 4,
            showServices: true,
          },
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      // Set default settings
      const defaultPrimary = '#2563eb';
      const defaultSecondary = '#1e40af';
      const defaultAccent = '#f59e0b';
      const defaultFontHeading = 'Inter';
      const defaultFontBody = 'Inter';
      applyThemeColors(defaultPrimary, defaultSecondary, defaultAccent);
      applyThemeFonts(defaultFontHeading, defaultFontBody);

      setSettings({
        general: {
          siteName: 'Auto & Noleggio Demo',
          siteSlogan: 'Il tuo partner di fiducia',
          siteDescription: 'Il tuo partner di fiducia per auto usate e noleggio',
          logo: '',
          logoDark: '',
          favicon: '',
          primaryColor: defaultPrimary,
          secondaryColor: defaultSecondary,
          accentColor: defaultAccent,
          fontHeading: defaultFontHeading,
          fontBody: defaultFontBody,
        },
        contact: {
          phone: '+39 02 1234567',
          phoneSecondary: '',
          email: 'info@demo.local',
          whatsapp: '+39 02 1234567',
          address: 'Via Roma 1',
          city: 'Milano',
          province: 'MI',
          cap: '20100',
          googleMapsUrl: '',
          businessHours: 'Lun-Ven: 9:00-18:00',
        },
        social: {
          facebook: '',
          instagram: '',
          youtube: '',
          linkedin: '',
          tiktok: '',
        },
        seo: {
          metaTitle: 'Auto & Noleggio Demo',
          metaDescription: 'Il tuo partner di fiducia per auto usate e noleggio',
          metaKeywords: 'auto, noleggio, vendita auto, auto usate',
          ogImage: '',
        },
        homepage: {
          heroTitle: '',
          heroSubtitle: '',
          heroImage: '',
          heroVideo: '',
          heroBackgroundType: 'gradient',
          heroBanner: {
            enabled: false,
            image: '',
            title: '',
            subtitle: '',
            link: '',
            linkText: '',
            position: 'right',
            style: 'card',
          },
          showStats: true,
          showFeaturedVehicles: true,
          featuredVehiclesCount: 4,
          showServices: true,
        },
      });
    }
  };

  const addFavorite = (id: string) => {
    if (!favorites.includes(id)) {
      setFavorites([...favorites, id]);
    }
  };

  const removeFavorite = (id: string) => {
    setFavorites(favorites.filter(f => f !== id));
  };

  const toggleFavorite = (id: string) => {
    if (favorites.includes(id)) {
      removeFavorite(id);
    } else {
      addFavorite(id);
    }
  };

  const isFavorite = (id: string) => favorites.includes(id);

  const addToCompare = (vehicle: CompareVehicle) => {
    if (!compareList.some(v => v.id === vehicle.id) && compareList.length < 3) {
      setCompareList([...compareList, vehicle]);
    }
  };

  const removeFromCompare = (id: string) => {
    setCompareList(compareList.filter(c => c.id !== id));
  };

  const isInCompare = (id: string) => compareList.some(v => v.id === id);

  const clearCompare = () => setCompareList([]);

  const contextValue: ShowroomContextType = {
    favorites,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite,
    compareList,
    addToCompare,
    removeFromCompare,
    isInCompare,
    clearCompare,
    settings,
  };

  return (
    <ShowroomContext.Provider value={contextValue}>
      {/* Dynamic SEO */}
      {settings && (
        <Helmet>
          <title>{settings.seo.metaTitle}</title>
          <meta name="description" content={settings.seo.metaDescription} />
          {settings.seo.metaKeywords && (
            <meta name="keywords" content={settings.seo.metaKeywords} />
          )}
          {/* Open Graph */}
          <meta property="og:title" content={settings.seo.metaTitle} />
          <meta property="og:description" content={settings.seo.metaDescription} />
          {settings.seo.ogImage && (
            <meta property="og:image" content={settings.seo.ogImage} />
          )}
          <meta property="og:type" content="website" />
          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={settings.seo.metaTitle} />
          <meta name="twitter:description" content={settings.seo.metaDescription} />
          {settings.seo.ogImage && (
            <meta name="twitter:image" content={settings.seo.ogImage} />
          )}
        </Helmet>
      )}

      <div className="min-h-screen flex flex-col bg-white">
        <PublicHeader />
        <main className="flex-1 pt-[120px] lg:pt-[110px]">
          <Outlet />
        </main>
        <PublicFooter />
        {compareList.length > 0 && <CompareBar />}

        {/* WhatsApp Floating Button */}
        {settings?.contact.whatsapp && (
          <WhatsAppButton phoneNumber={settings.contact.whatsapp} />
        )}
      </div>
    </ShowroomContext.Provider>
  );
}
